import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormConfig} from '../../models/form-config';
import {FieldConfig} from '../../models/field-config';
import {AbstractControl, FormGroup} from '@angular/forms';
import {FieldType} from '../../models/field-type';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {TranslateService} from '@ngx-translate/core';
import * as moment_ from 'moment';
import {DateFormat} from '../esp-datefield/models/date-formats';
import {TextTransformService} from '../../services/text-transform.service';
import {VehicleVersionData} from '../../../core/services/impl/section-services/vehicle-model/vehicle.service';
import {VehicleModelConsts} from '../../../core/consts/vehicle-model.consts';
import {VehicleSourceConsts} from '../../../core/consts/vehicle-source.consts';
import {SectionsStateService, SectionState} from '../../services/sections-state.service';
import {SectionComponent} from '../section/section.component';
import {isNext, isPromise} from '../../../core/utils/utils';
import {InsuranceDataEnum} from '../../../core/consts/insurance-data.enum';
import {VehicleModelStateService} from '../../../core/services/impl/section-services/vehicle-model/vehicle-model-state.service';
import {MaterialDatepickerType} from '../../enums/material-datepicker-type.enum';
import {SectionComponentKey} from '../../../core/enums/components-keys';

const moment = moment_;

@Component({
  selector: 'app-section-data-presentation',
  templateUrl: './section-data-presentation.component.html'
})
export class SectionDataPresentationComponent implements OnInit, AfterViewInit {

  @Input() parentSection: SectionComponent;
  @Input() formGroup: FormGroup;
  @Input() formConfig: FormConfig;
  @Input() displayedFields: string[];

  @Output() editModeChange: EventEmitter<void> = new EventEmitter<void>();
  @Output() afterViewInit: EventEmitter<void> = new EventEmitter<void>();

  public presentationData: any[];
  isNext = isNext();

  get vehicleModelSectionState(): SectionState {
    return this.sectionsStateService.sectionState(SectionComponentKey.VEHICLE_MODEL);
  }

  get shouldFillFromService(): boolean {
    return this.formConfig.formKey === 'VEHICLE_MODEL_SECTION' && !isNotNullOrUndefined(this.vehicleModelSectionState.createdAtLeastOnce);
  }

  get editIcon(): string {
    return './assets/images/icons/edit.svg';
  }

  get editButtonLabel(): string {
    return this.isNext ? 'SECTION-DATA-PRESENTATION.edit' : 'SECTION-DATA-PRESENTATION.change';
  }

  constructor(private translate: TranslateService,
              private sectionsStateService: SectionsStateService,
              private vehicleModelStateService: VehicleModelStateService) {
  }

  ngOnInit() {
    this.prepareData();
    if (this.formConfig.formKey === 'VEHICLE_MODEL_SECTION') {
      this.vehicleModelSectionState.createdAtLeastOnce = true;
      if (this.vehicleModelStateService.unknownRegistrationNumber) {
        this.parentSection.editModeChange();
      }
    }
  }

  ngAfterViewInit(): void {
    this.afterViewInit.emit();
  }

  changeEditMode() {
    this.parentSection.editModeChange();
    this.editModeChange.emit();
  }

  public async prepareData() {
    this.presentationData = [];
    if (this.shouldFillFromService) {
      this.fillVehicleModelSectionFromService();
      return;
    }
    for (const field of this.formConfig.fieldsConfig) {
      await this.prepareDataFromConfig(field);
    }
  }

  public getControl(fieldKey: string): AbstractControl {
    return this.formGroup.controls[fieldKey];
  }

  private async prepareDataFromConfig(field: FieldConfig) {
    if (field.displayInPreviewMode && this.externalFieldCondition(field.fieldKey)) {
      const controlValue = this.getControl(field.fieldKey).value;
      const fieldLabelKey = field.captionForPreview ? field.captionForPreview : field.caption;
      const fieldLabel = fieldLabelKey ? this.translate.instant(fieldLabelKey) : '';

      if (isNotNullOrUndefined(controlValue)) {
        switch (field.type) {
          case FieldType.Select:
            try {
              const item = await this.getItemForSelect(field);
              let fieldValue;
              if (item) {
                const valueProperty = Array.isArray(field.options['label'].label) ? field.options['label']['labelConfig']['bindLabel'] : field.options['label'].label;
                fieldValue = item[valueProperty];
              } else {
                fieldValue = controlValue;
              }
              if (field.options['label'] && field.options['label']['labelConfig'] && field.options['label']['labelConfig']['suffix']) {
                fieldValue += field.options['label']['labelConfig']['suffix'];
              }
              this.pushData(fieldLabel, fieldValue, field);
            } catch (e) {
              this.pushData(fieldLabel, controlValue, field);
            }
            break;
          case FieldType.Radio:
            let foundItem = field.radioConfig.find(option => option.value === controlValue);
            if (foundItem) {
              this.pushData(fieldLabel, this.translate.instant(foundItem.label), field);
            }
            break;
          case FieldType.ButtonToggle:
            let foundItemButton = field.radioConfig.find(option => option.value === controlValue);
            if (foundItemButton) {
              this.pushData(fieldLabel, this.translate.instant(foundItemButton.label), field);
            }
            break;
          case FieldType.Checkbox:
            const checkboxValue = controlValue ? 'Tak' : 'Nie';
            this.pushData(fieldLabel, checkboxValue, field);
            break;
          case FieldType.DateField:
            if (field.materialDateFieldConfig.type === MaterialDatepickerType.YEAR) {
              this.pushData(fieldLabel, moment(controlValue).year(), field);
            } else {
              this.pushData(fieldLabel, moment(controlValue).format(DateFormat.DD_MM_YYYY_dot), field);
            }
            break;
          case FieldType.TextField:
          case FieldType.TextArea:
            let textValue = controlValue;
            if (field.properties && field.properties.textTransform && field.properties.textTransform.separatorConfig) {
              textValue = TextTransformService.separateValues(controlValue, field.properties.textTransform.separatorConfig);
            }
            this.pushData(fieldLabel, textValue, field);
            break;
          default:
            this.pushData(fieldLabel, '-', field);
            break;
        }
      } else {
        this.pushData(fieldLabel, '-', field);
      }
    }
  }

  private fillVehicleModelSectionFromService(): void {
    const eurotaxVersionCode = this.getControl(VehicleModelConsts.VEHICLE_CODE_EUROTAX).value;
    const infoexpertLink4VersionCode = this.getControl(VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT).value;
    const fieldsFromControls = [VehicleModelConsts.REGISTRATION_NUMBER, VehicleModelConsts.PRODUCTION_YEAR, InsuranceDataEnum.START_INSURANCE_DATE];
    const eurotaxData = this.vehicleModelStateService.requestVehicleVersionData(eurotaxVersionCode, VehicleSourceConsts.EUROTAX);
    const promises = [eurotaxData];
    if (isNotNullOrUndefined(infoexpertLink4VersionCode)) {
      const infoexpertLink4Data = this.vehicleModelStateService.requestVehicleVersionData(infoexpertLink4VersionCode, VehicleSourceConsts.INFOEXPERT_LINK4);
      promises.push(infoexpertLink4Data);
    }
    Promise.all(promises)
      .then(async (vehicleData: VehicleVersionData[]) => {
        for (const field of this.formConfig.fieldsConfig.filter(fieldConfig => fieldConfig.displayInPreviewMode)) {
          if (fieldsFromControls.includes(field.fieldKey)) {
            await this.prepareDataFromConfig(field);
          } else {
            const fieldLabel = field.caption ? this.translate.instant(field.caption) : '';
            let fieldValue;
            switch (field.fieldKey) {
              case VehicleModelConsts.BRAND_CODE:
                fieldValue = vehicleData[0].brandName;
                break;
              case VehicleModelConsts.MODEL_CODE:
                fieldValue = vehicleData[0].modelName;
                break;
              case VehicleModelConsts.FUEL_CODE:
                fieldValue = vehicleData[0].fuelName;
                break;
              case VehicleModelConsts.ENGINE_CAPACITY:
                fieldValue = vehicleData[0].engineCapacity;
                break;
              case VehicleModelConsts.VEHICLE_CODE_EUROTAX:
                fieldValue = vehicleData[0].name;
                break;
              case VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT:
                fieldValue = infoexpertLink4VersionCode && isNotNullOrUndefined(vehicleData[1]) ? vehicleData[1].name : '-';
                break;
              default:
                fieldValue = '-';
            }
            // FIXME refactor duplicated logic (quick fix for engine capacity formatting)
            if (field.options && field.options['label'] && field.options['label']['labelConfig'] && field.options['label']['labelConfig']['suffix']) {
              fieldValue += field.options['label']['labelConfig']['suffix'];
            }
            this.pushData(fieldLabel, fieldValue, field);
          }
        }
      });
  }

  private getItemForSelect(field: FieldConfig): Promise<any> {
    return new Promise(resolve => {
      const controlValue = this.getControl(field.fieldKey).value;
      const dictionary = field.options['items'];
      const valueProperty = field.options['item'];
      if (field.select.async && isPromise(dictionary)) {
        dictionary.then(items => {
          const item = items.find(option => option[valueProperty] === controlValue);
          resolve(item);
        });
      } else {
        const item = (dictionary as Array<any>).find(option => option[valueProperty] === controlValue);
        resolve(item);
      }
    });
  }

  private externalFieldCondition(fieldKey: string) {
    if (this.displayedFields && this.displayedFields.length > 0) {
      return this.displayedFields.includes(fieldKey)
    }
    return true;
  }

  private pushData(label, value, fieldConfig): void {
    const fullWidth = fieldConfig.fullWidthInPreviewMode ? fieldConfig.fullWidthInPreviewMode : false;
    this.presentationData.push({ key: label, value: value, fullWidth: fullWidth });
  }
}
